import React from 'react';
import { css } from '@emotion/react';
import { ReviewedStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/booking_submission.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { ScheduleQueries } from '@frontend/api-schedule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { getUser, hasSchemaACL } from '@frontend/auth-helpers';
import { ActionsUI } from '@frontend/contact-actions';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import {
  Avatar,
  ContentLoader,
  PopoverMenu,
  PopoverMenuItem,
  Text,
  useModalControl,
  usePopoverMenu,
  useAlert,
  ConfirmationModal,
  Button,
  currency,
  formatDate,
  Chip,
} from '@frontend/design-system';
import { useScheduleActionsContext } from '../../ScheduleActionsContext';
import { useScheduleRequestContext } from '../Context/ScheduleRequestContext';
import { ScheduleRequestsTrackingIds } from '../trackingIds';
import { ScheduleRequestDeleteConfirmationModal } from './components/ScheduleRequestDeleteConfirmationModal';

interface Props {
  requestVersion: string;
  name: string;
  personId?: string;
  appointmentTime: string;
  personPhone: string;
  paidAmount: number;
  locationId: string;
  updateSelectedScheduleRequest: () => void;
  scheduleRequestId: string;
  markNotificationsAsRead: (val: string) => void;
  showLocationName?: boolean;
}

export const ScheduleRequestListItemHeader = React.memo(
  ({
    requestVersion,
    name,
    paidAmount,
    personId,
    appointmentTime,
    personPhone,
    locationId,
    scheduleRequestId,
    updateSelectedScheduleRequest,
    markNotificationsAsRead,
    showLocationName = false,
  }: Props) => {
    const alert = useAlert();
    const user = getUser();
    const { t } = useTranslation('scheduleCalendarActions');
    const { getLocationName } = useAppScopeStore();
    const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
    const shouldUseV3 = requestVersion !== 'v2' && isScheduleV3FlagEnabled;

    const { refetchScheduleRequestList } = useScheduleRequestContext();
    const { closeModal, refetchAppointmentRequestCount } = useScheduleActionsContext();

    const deleteScheduleRequestModalProps = useModalControl();
    const rejectScheduleRequestModalControl = useModalControl();

    const { mutateAsync: deleteScheduleRequest, isLoading: isLoadingDeleteScheduleRequest } =
      ScheduleQueries.useDeleteScheduleRequest();
    const { mutateAsync: deleteScheduleRequestV3, isLoading: isLoadingDeleteScheduleRequestV3 } =
      SchedulerV3.Mutations.useDeleteBookingSubmissionMutation();

    const { mutate: rejectScheduleRequestV3, isLoading: isRejectingScheduleRequestV3 } =
      SchedulerV3.Mutations.useUpdateBookingSubmissionMutation({
        options: {
          onSuccess: () => {
            rejectScheduleRequestModalControl.closeModal();
            alert.success(t('Schedule request rejected successfully'));
          },
          onError: () => {
            alert.error(t('Failed to reject schedule request'));
          },
        },
      });

    const { onClick: messageOnClick } = ActionsUI.actions.useMessageAction();
    const { featureFlags } = useAppFlagStore();
    const canSendMessages = featureFlags[locationId]?.get('nwx:messaging-integrated-messaging-fe-acl-enforcement')
      ?.value
      ? !!hasSchemaACL(locationId, Permission.SMS_INBOX_MANAGE)
      : true;

    const {
      getMenuProps,
      getTriggerProps,
      close: closePopOverMenu,
    } = usePopoverMenu({
      placement: 'bottom-start',
    });

    const rejectScheduleRequest = () => {
      rejectScheduleRequestV3({
        submissionId: scheduleRequestId,
        reviewedBy: user?.userID ?? '',
        reviewedStatus: ReviewedStatus.REJECTED,
      });
    };

    const deleteScheduleRequestHandler = async () => {
      try {
        if (shouldUseV3) {
          await deleteScheduleRequestV3({ submissionIds: [scheduleRequestId] });
        } else {
          await deleteScheduleRequest({ id: scheduleRequestId, locationId });
          refetchAppointmentRequestCount();
          refetchScheduleRequestList();
        }
        deleteScheduleRequestModalProps.closeModal();
        alert.success(t('Schedule request deleted successfully'));
      } catch (error) {
        alert.error(t('Failed to delete schedule request'));
      }
    };

    const handleReviewRequestMenuItemClick = () => {
      updateSelectedScheduleRequest();
      markNotificationsAsRead(scheduleRequestId);
      closePopOverMenu();
    };

    const handleRejectRequestMenuItemClick = () => {
      rejectScheduleRequestModalControl.openModal();
      markNotificationsAsRead(scheduleRequestId);
      closePopOverMenu();
    };
    const handleDeleteMenuItemClick = () => {
      deleteScheduleRequestModalProps.openModal();
      markNotificationsAsRead(scheduleRequestId);
      closePopOverMenu();
    };
    const handleMessageButtonClick = async () => {
      await messageOnClick({
        personId,
        personPhone,
        threadGroupId: locationId,
        groupIds: [locationId],
      });
      markNotificationsAsRead(scheduleRequestId);
      closePopOverMenu();
      closeModal();
    };

    const locationName = getLocationName(locationId);

    return (
      <>
        <ContentLoader
          show={isLoadingDeleteScheduleRequest || isLoadingDeleteScheduleRequestV3 || isRejectingScheduleRequestV3}
        />
        <div css={containerStyles}>
          <div css={requestInfoStyle}>
            <Avatar name={name} />
            <div css={{ marginLeft: theme.spacing(2) }}>
              <Text size='medium' textAlign='left' weight='bold'>
                {name}
              </Text>
              {!!paidAmount && (
                <>
                  <Text textAlign='left' size='small' css={{ display: 'inline', color: theme.colors.tangerine50 }}>
                    {currency(paidAmount, { decimals: 2 })}
                  </Text>
                  {` • `}
                </>
              )}
              <Text textAlign='left' size='small' css={{ display: 'inline' }}>
                {/* For v3, appointmentTime contains request created date and time which should be in user's timezone */}
                {formatDate(appointmentTime, 'MMM D YYYY, h:mm A')}
              </Text>
            </div>
          </div>
          {showLocationName && locationName && <Chip.Location variant='neutral'>{locationName}</Chip.Location>}
          <Button
            variant='secondary'
            iconName='more-small'
            trackingId={ScheduleRequestsTrackingIds.requestListItemMoreBtn}
            {...getTriggerProps()}
          />
          <PopoverMenu {...getMenuProps()}>
            <PopoverMenuItem
              trackingId={ScheduleRequestsTrackingIds.requestListItemReviewRequestBtn}
              Icon={() => <Icon name='lists-small' />}
              onClick={handleReviewRequestMenuItemClick}
            >
              {t('Review Request')}
            </PopoverMenuItem>
            <div>
              {personPhone && (
                <PopoverMenuItem
                  trackingId={ScheduleRequestsTrackingIds.requestListItemMessageBtn}
                  Icon={() => <Icon name='message-small' />}
                  css={{ width: '100%' }}
                  onClick={handleMessageButtonClick}
                  disabled={!canSendMessages}
                  hoverLabel={canSendMessages ? undefined : t('You do not have permission to send text messages')}
                >
                  {t('Message')}
                </PopoverMenuItem>
              )}
            </div>
            {shouldUseV3 && (
              <PopoverMenuItem
                trackingId={ScheduleRequestsTrackingIds.requestListItemRejectBtn}
                Icon={() => <Icon name='calendar-discarded-small' />}
                onClick={handleRejectRequestMenuItemClick}
              >
                {t('Reject Request')}
              </PopoverMenuItem>
            )}
            <PopoverMenuItem
              trackingId={ScheduleRequestsTrackingIds.requestListItemDeleteBtn}
              Icon={() => <Icon name='trash-small' />}
              onClick={handleDeleteMenuItemClick}
            >
              {t('Delete')}
            </PopoverMenuItem>
          </PopoverMenu>
        </div>
        <ScheduleRequestDeleteConfirmationModal
          onConfirm={deleteScheduleRequestHandler}
          modalProps={deleteScheduleRequestModalProps.modalProps}
        />
        <ConfirmationModal
          destructive
          {...rejectScheduleRequestModalControl.modalProps}
          title={t('Confirm Reject')}
          message={t('Are you sure you want to reject this request?')}
          onConfirm={rejectScheduleRequest}
          cancelTrackingId={ScheduleRequestsTrackingIds.rejectConfirmationModalCancelBtn}
          confirmTrackingId={ScheduleRequestsTrackingIds.rejectConfirmationModalConfirmBtn}
        />
      </>
    );
  }
);

ScheduleRequestListItemHeader.displayName = 'ScheduleRequestListItemHeader';

const containerStyles = css({ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' });
const requestInfoStyle = css({ display: 'flex', alignItems: 'center' });
